import React, { useState } from 'react';
import { Alert } from '@mui/material';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import {
    useContractDetail,
    useContractDetailData,
} from '../../../../contextsProperty/ContextContractDetail';
import useTranslate from '../../../../../customHooks/useTranslate';
import SwitchInput from '../../../../../muiComponents/switchInput/SwitchInput';
import IconButton from '../../../../../muiComponents/IconButton';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import {
    doubleMD_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';
import LoadedOptionsTypeahead from '../../../../../muiComponents/LoadedOptionsTypeahead';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import Title from '../../../../components/Title';
import Button from '../../../../../muiComponents/Button';
import { useODataMultipleQueries } from '../../../../../customHooks/useODataMultipleQueries';
import { readOnlyEntities } from '../../../../../utils/commonVariables/entityKeys';
import { ISupplierHotelMealPlan } from '../../../../../types/entities/ISupplierHotelMealPlan';
import Icon from '../../../../../muiComponents/Icon';
import { MEAL_PLAN_VALIDATION_INITIAL_STATE } from '../../../../../utils/contractUtils/contractUtils';
import { validateForm } from '../../../../../utils/validateForms';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import DeletePopUp from '../DeletePopUp';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import LoadingPanel from './LoadingPanel';
import PanelTitle from '../../../yourProperties/Title';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../../ReadOnlyUserAlert';

interface MealPlanPanelProps {
    open: boolean;
    onClose: () => void;
}

const MealPlanPanel = (props: MealPlanPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { readOnlyUser } = useUserProfile();
    const [deletePopUpVisible, setDeletePopUpVisible] = useState(false);
    const {
        isLoadingPanel,
        handleLoadingPanel,
        isBlockedContract,
        mealPlanEdition,
        handleChangeMealPlanEdition,
        handleSaveMealPlan,
    } = useContractDetail();
    const { idSupplierHotel, mealPlans } = useContractDetailData();

    const isNewMealPlan =
        !mealPlanEdition.id || parseInt(mealPlanEdition.id) <= 0;

    const { supplierHotelMealPlans } = useODataMultipleQueries({
        queries: [
            {
                key: 'supplierHotelMealPlans',
                endpoint: {
                    entityName: readOnlyEntities.SupplierHotelMealPlan,
                    select: 'Id,Name,IsRoomOnly,IsBedAndBreakfast,IdSupplierHotel,MealPlanType/Name',
                    orderBy: 'Name',
                    expand: 'MealPlanType',
                    filter: [
                        `IdSupplierHotel eq ${idSupplierHotel}`,
                        'Enabled',
                    ],
                },
            },
        ],
        dependencies: [idSupplierHotel],
        condition: !!idSupplierHotel,
    });

    const remainingMealPlansOptions = supplierHotelMealPlans.data.filter(
        (supHotMealPlan: ISupplierHotelMealPlan) =>
            !mealPlans.some(
                mp => mp.idMealPlan === parseInt(supHotMealPlan.Id)
            ) ||
            (!isNewMealPlan &&
                parseInt(supHotMealPlan.Id) === mealPlanEdition.idMealPlan)
    );

    const [validatedForm, setValidatedForm] = useState(false);
    const [formErrors, setFormErrors] = useState(
        MEAL_PLAN_VALIDATION_INITIAL_STATE
    );

    const fieldsToValidate = {
        name: {
            value: mealPlanEdition.name,
            validators: ['requiredString'],
        },
        idSupplierHotelMealPlan: {
            value: mealPlanEdition.idMealPlan,
            validators: ['requiredIdEntity'],
        },
    };

    const validateInputsForm = () => {
        setFormErrors(MEAL_PLAN_VALIDATION_INITIAL_STATE);
        const errors = validateForm(fieldsToValidate);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    return (
        <>
            {deletePopUpVisible && !readOnlyUser && (
                <DeletePopUp
                    open={!!deletePopUpVisible}
                    onSave={async () => {
                        try {
                            handleLoadingPanel(true);
                            await handleSaveMealPlan({
                                ...mealPlanEdition,
                                Deleted: true,
                            });
                            setSuccessMessage(
                                FeedbackMessages.COMMON_SUCCESS_DELETE
                            );
                        } catch (error) {
                            setErrorMessage(FeedbackMessages.COMMON_ERROR);
                        } finally {
                            handleLoadingPanel(false);
                            setDeletePopUpVisible(false);
                            onClose();
                        }
                    }}
                    onCancel={() => {
                        setDeletePopUpVisible(false);
                    }}
                ></DeletePopUp>
            )}
            <LateralPanel open={open}>
                {isLoadingPanel ? (
                    <LoadingPanel />
                ) : (
                    <form className='flex-column height100perc flex-between'>
                        <div className='flex-column  gap-30'>
                            <div className='flex-between flex-align-center contract-panel__title'>
                                <PanelTitle
                                    text={
                                        isNewMealPlan
                                            ? 'new_meal_plan'
                                            : mealPlanEdition.name
                                    }
                                    className='text-secondary-color'
                                    translated={isNewMealPlan}
                                />
                                <div className='flex-between gap-10'>
                                    <SwitchInput
                                        labelText={''}
                                        checked={mealPlanEdition.enabled}
                                        disabled={
                                            isBlockedContract || readOnlyUser
                                        }
                                        onChangeMethod={() => {
                                            handleChangeMealPlanEdition({
                                                enabled:
                                                    !mealPlanEdition.enabled,
                                            });
                                        }}
                                    />
                                    <IconButton
                                        iconName='Close'
                                        onClick={onClose}
                                    />
                                </div>
                            </div>

                            {readOnlyUser ? (
                                <ReadOnlyUserAlert text='read_only_contract' />
                            ) : (
                                isBlockedContract && (
                                    <Alert severity='info'>
                                        {translate(
                                            'bloqued_information_contract'
                                        )}
                                    </Alert>
                                )
                            )}
                            <div className='flex-column  gap-10 '>
                                <Grid
                                    className='border-color-block padding-block-20'
                                    style={{ width: 'auto' }}
                                    columnSpacing={gridSpacing30}
                                    rowSpacing={gridSpacing20}
                                >
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <LoadedOptionsTypeahead
                                            required
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            id='idSupplierMealPlan'
                                            gridProps={doubleMD_ColumnGrid}
                                            options={remainingMealPlansOptions}
                                            labelText='meal_plan'
                                            idValue={mealPlanEdition.idMealPlan}
                                            handleChange={(
                                                _event: React.SyntheticEvent,
                                                value: ISupplierHotelMealPlan | null
                                            ) => {
                                                handleChangeMealPlanEdition({
                                                    name: value?.Name || '',
                                                    idMealPlan: value
                                                        ? parseInt(value?.Id)
                                                        : 0,
                                                    isBedAndBreakfast:
                                                        value?.IsBedAndBreakfast ||
                                                        false,
                                                    isRoomOnly:
                                                        value?.IsRoomOnly ||
                                                        false,
                                                    __isChanged: true,
                                                });
                                            }}
                                            error={
                                                !!formErrors.idSupplierHotelMealPlan
                                            }
                                            errorMessage={
                                                formErrors.idSupplierHotelMealPlan
                                            }
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                        />
                                    </GridChild>
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <TextInput
                                            required
                                            id='name'
                                            labelText='name'
                                            value={mealPlanEdition.name}
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            handleChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleChangeMealPlanEdition({
                                                    name: e.target.value,
                                                    __isChanged: true,
                                                })
                                            }
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            error={!!formErrors.name}
                                            errorMessage={formErrors.name}
                                        />
                                    </GridChild>
                                </Grid>
                                {mealPlanEdition.idMealPlan &&
                                (mealPlanEdition.isRoomOnly ||
                                    mealPlanEdition.isBedAndBreakfast) ? (
                                    <div
                                        className='contract-panel__bottom-line'
                                        style={{
                                            width: 'auto',
                                            paddingTop: '15px',
                                        }}
                                    >
                                        {mealPlanEdition.isRoomOnly && (
                                            <div
                                                style={{
                                                    width: '50%',
                                                    padding: '15px',
                                                }}
                                                className=' gap-10 flex-align-center'
                                            >
                                                <Icon
                                                    iconName={'InfoOutlined'}
                                                    sx={{
                                                        color: '#ffc107',
                                                        width: '16px',
                                                        height: '16px',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                                <Title>
                                                    {translate('room_only')}
                                                </Title>
                                            </div>
                                        )}
                                        {mealPlanEdition.isBedAndBreakfast && (
                                            <div
                                                style={{
                                                    width: '50%',
                                                    padding: '15px',
                                                }}
                                                className=' gap-10 flex-align-center'
                                            >
                                                <Icon
                                                    iconName={'InfoOutlined'}
                                                    sx={{
                                                        color: '#ffc107',
                                                        width: '16px',
                                                        height: '16px',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                                <Title>
                                                    {'Bed & Breakfast'}
                                                </Title>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div
                            className={`flex-${
                                isNewMealPlan ? 'end' : 'between'
                            }`}
                        >
                            {!isNewMealPlan && (
                                <Button
                                    className='delete-button'
                                    onClick={() => {
                                        setDeletePopUpVisible(true);
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('delete')}
                                </Button>
                            )}

                            <div className='flex-end gap-20'>
                                <Button
                                    variant='outlined'
                                    onClick={onClose}
                                    disabled={isLoadingPanel}
                                >
                                    {translate('cancel')}
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={async () => {
                                        setValidatedForm(true);
                                        const validated = validateInputsForm();
                                        if (validated) {
                                            await handleSaveMealPlan();
                                        }
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('save')}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </LateralPanel>
        </>
    );
};

export default MealPlanPanel;
